import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProjectDocumentDetails from "../../components/projects/projectBilling/ProjectDocumentDetails";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { AddTask, CheckCircle, Edit, Payments } from "@mui/icons-material";
import ProjectBillingDetails from "../../components/projects/projectBilling/ProjectBillingDetails";
import BTAllocation from "../../components/projects/projectBilling/BTAllocation";
import BillingDialog from "../../components/projects/projectBilling/BillingDialog";
import BillingWarningDialog from "../../components/projects/projectBilling/BillingWarningDialog";
import BTAllocationDialog from "../../components/projects/projectBilling/BTAllocationDialog";
import ProjectCompletionNoteCard from "../../components/projects/ProjectCompletionNoteCard";
import ActivityFormDialog from "../../components/activities/ActivityFormDialog";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { CrmContactStatus } from "../../types/companyTypes";
import { ActivityDto, ActivityType, CreateActivityDto } from "../../types/activityTypes";
import { MemberBTAllocationDto } from "../../types/projectTypes";
import useProjectsDetails from "../../hooks/projects/useProjectsDetails";
import useActivityMutations from "../../hooks/activities/useActivityMutations";
import LoadingCircle from "../../components/general/LoadingCircle";
import {
  canCompleteProject,
  PROJECT_COMPLETION_NOTE_MIN_LENGTH,
  PROJECT_CLOSURE_WAIVER_REASON_MIN_LENGTH,
} from "../../utils/projectStatusUtils";
import dayjs, { Dayjs } from "dayjs";

/**
 * ProjectBilling component to display the billing of a project
 * @returns - A container with the billing of a project
 */
const ProjectBilling = () => {
  const { id } = useParams();
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();

  const {
    projectDetails,
    isProjectDetailsLoading,
    projectBillingDetails,
    isProjectBillingDetailsLoading,
    startBilling,
    completeProject,
    updateProjectCompletionNote,
    updateProjectClosureWaiver,
    changeProjectBillingDocumentStatus,
    changeProjectMemberBillingDetails,
    saveBTAllocation,
  } = useProjectsDetails(Number(id));
  const { createActivity: createFollowUpActivity, isCreatingActivity: isCreatingFollowUpActivity } =
    useActivityMutations();

  const [billingDialogOpen, setBillingDialogOpen] = React.useState(false);
  const [billingWarningDialogOpen, setBillingWarningDialogOpen] = React.useState(false);
  const [btAllocationDialogOpen, setBTAllocationDialogOpen] = React.useState(false);
  const [projectCompletionNoteDraft, setProjectCompletionNoteDraft] = React.useState("");
  const [projectClosureWaiverDraft, setProjectClosureWaiverDraft] = React.useState("");
  const [waiveFollowUpActivity, setWaiveFollowUpActivity] = React.useState(false);
  const [followUpActivity, setFollowUpActivity] = React.useState<ActivityDto | null>(null);
  const [isSavingProjectCompletionNote, setIsSavingProjectCompletionNote] = React.useState(false);
  const [isSavingProjectClosureWaiver, setIsSavingProjectClosureWaiver] = React.useState(false);
  const [isCompletingProject, setIsCompletingProject] = React.useState(false);
  const [followUpDialogOpen, setFollowUpDialogOpen] = React.useState(false);
  const [followUpActivityType, setFollowUpActivityType] = React.useState<ActivityType>(ActivityType.CALL);
  const [followUpActivityTitle, setFollowUpActivityTitle] = React.useState("Folgeaktivität nach Projektabschluss");
  const [followUpActivityDescription, setFollowUpActivityDescription] = React.useState("");
  const [followUpResponsibleUserId, setFollowUpResponsibleUserId] = React.useState<number | "">("");
  const [followUpDeadline, setFollowUpDeadline] = React.useState<Dayjs | null>(dayjs().add(14, "day"));
  const [followUpActivityTypeError, setFollowUpActivityTypeError] = React.useState(false);
  const [followUpActivityTitleError, setFollowUpActivityTitleError] = React.useState(false);
  const [followUpResponsibleUserError, setFollowUpResponsibleUserError] = React.useState(false);
  const [followUpDeadlineError, setFollowUpDeadlineError] = React.useState(false);
  const hasPermissionDocumentFiling = doesPermissionsHaveSomeOf(auth.permissions, [7]);
  const hasPermissionContractFiling = doesPermissionsHaveSomeOf(auth.permissions, [11]);
  const hasPermissionProjectBilling = doesPermissionsHaveSomeOf(auth.permissions, [22]);
  const hasProjectPermission = doesPermissionsHaveSomeOf(auth.permissions, [19]);
  const isPL = projectDetails?.members?.some((member) => member.memberId === auth.userID && member.type === "PL");
  const canSeePage =
    doesPermissionsHaveSomeOf(auth.permissions, [19, 22, 7, 13, 11]) ||
    projectDetails?.members?.some((member) => member.memberId === auth.userID) ||
    projectDetails?.qms?.some((member) => member.memberId === auth.userID);
  const projectStatus = projectBillingDetails?.status ?? projectDetails?.status;
  const savedProjectCompletionNote =
    projectBillingDetails?.projectCompletionNote ?? projectDetails?.projectCompletionNote ?? "";
  const savedProjectClosureWaiverReason =
    projectBillingDetails?.projectClosureWaiverReason ?? projectDetails?.projectClosureWaiverReason ?? "";
  const savedPlannedFollowUpActivity =
    projectBillingDetails?.plannedFollowUpActivity ?? projectDetails?.plannedFollowUpActivity ?? false;
  const closureActivityMembers = React.useMemo(() => {
    const members =
      projectDetails?.members
        ?.filter((member) => member.type !== null && member.type !== "Bewerbung")
        .map((member) => ({
          memberId: member.memberId,
          firstname: member.firstname,
          lastname: member.lastname,
        })) ?? [];
    const qms = projectDetails?.qms ?? [];
    const merged = [...members, ...qms];

    return merged.filter(
      (member, index, array) => array.findIndex((candidate) => candidate.memberId === member.memberId) === index
    );
  }, [projectDetails?.members, projectDetails?.qms]);

  useEffect(() => {
    // Reset the local draft whenever the persisted note changes after a save/refetch.
    setProjectCompletionNoteDraft(savedProjectCompletionNote);
  }, [savedProjectCompletionNote]);

  useEffect(() => {
    setProjectClosureWaiverDraft(savedProjectClosureWaiverReason);
    setWaiveFollowUpActivity(savedProjectClosureWaiverReason.trim().length > 0);
  }, [savedProjectClosureWaiverReason]);

  useEffect(() => {
    if (followUpResponsibleUserId !== "" || closureActivityMembers.length === 0) {
      return;
    }

    const preferredResponsibleUser =
      closureActivityMembers.find((member) => member.memberId === auth.userID)?.memberId ??
      closureActivityMembers[0]?.memberId ??
      "";
    setFollowUpResponsibleUserId(preferredResponsibleUser);
  }, [auth.userID, closureActivityMembers, followUpResponsibleUserId]);

  if (isProjectDetailsLoading || isProjectBillingDetailsLoading) {
    return <LoadingCircle />;
  }

  // Check for correct permission
  if (!canSeePage) {
    return (
      <Container>
        <Stack>
          <Typography variant="h5" fontWeight={"bold"}>
            Keine Berechtigung
          </Typography>
          <Typography variant="body1">Sie haben keine Berechtigung diese Seite zu sehen.</Typography>
        </Stack>
      </Container>
    );
  }

  // Check for correct project status
  if (projectStatus != "Durchführung" && projectStatus != "Abrechnung" && projectStatus != "Abgeschlossen") {
    return (
      <Stack>
        <Typography fontWeight={"bold"}>Projektstatus falsch</Typography>
        <Typography variant="body1">Das Projekt befindet sich nicht in Abrechnung.</Typography>
      </Stack>
    );
  }

  // handle billing dialog open
  const handleOpenBillingDialog = () => {
    if (
      !projectBillingDetails?.signatureDate ||
      projectBillingDetails?.euroPerBT === null ||
      projectBillingDetails?.euroPerBT < 1 ||
      projectBillingDetails?.soldBT === null ||
      projectBillingDetails?.soldBT < 0 ||
      projectBillingDetails?.soldExpenses === null ||
      projectBillingDetails?.soldExpenses < 0
    ) {
      setBillingWarningDialogOpen(true);
    } else {
      setBillingDialogOpen(true);
    }
  };

  // handle billing dialog close
  const handleCloseBillingDialog = () => {
    setBillingDialogOpen(false);
  };

  // handle start billing
  const handleStartBilling = () => {
    startBilling();
    setBillingDialogOpen(false);
  };

  // handle billing warning dialog close
  const handleCloseBillingWarning = () => {
    setBillingWarningDialogOpen(false);
    navigate(`/projekte/${id}`);
  };

  // handle open BT allocation dialog
  const handleOpenBTAllocationDialog = () => {
    setBTAllocationDialogOpen(true);
  };

  // handle close BT allocation dialog
  const handleCloseBTAllocationDialog = () => {
    setBTAllocationDialogOpen(false);
  };

  // handle save BT allocation
  const handleSaveBTAllocation = (allocations: MemberBTAllocationDto[]) => {
    saveBTAllocation(allocations);
    setBTAllocationDialogOpen(false);
  };

  const handleSaveProjectCompletionNote = async () => {
    setIsSavingProjectCompletionNote(true);
    try {
      await updateProjectCompletionNote(projectCompletionNoteDraft);
    } finally {
      setIsSavingProjectCompletionNote(false);
    }
  };

  const hasPlannedFollowUpActivity = savedPlannedFollowUpActivity || Boolean(followUpActivity);

  const handleSaveProjectClosureWaiver = async () => {
    setIsSavingProjectClosureWaiver(true);
    try {
      await updateProjectClosureWaiver(
        waiveFollowUpActivity ? projectClosureWaiverDraft.trim() : "",
        waiveFollowUpActivity ? false : savedPlannedFollowUpActivity
      );
    } finally {
      setIsSavingProjectClosureWaiver(false);
    }
  };

  const handleOpenFollowUpDialog = () => {
    if (
      projectDetails?.contactPerson?.crmContactAllowed === false ||
      projectDetails?.contactPerson?.crmContactStatus === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED ||
      hasPlannedFollowUpActivity ||
      waiveFollowUpActivity
    ) {
      return;
    }

    setFollowUpActivityType(ActivityType.CALL);
    setFollowUpActivityTitle("Folgeaktivität nach Projektabschluss");
    setFollowUpActivityDescription("");
    setFollowUpDeadline(dayjs().add(14, "day"));
    setFollowUpActivityTypeError(false);
    setFollowUpActivityTitleError(false);
    setFollowUpResponsibleUserError(false);
    setFollowUpDeadlineError(false);
    setFollowUpDialogOpen(true);
  };

  const handleCreateFollowUpActivity = async () => {
    const nextTypeError = !followUpActivityType;
    const nextTitleError = !followUpActivityTitle.trim();
    const nextResponsibleUserError = !followUpResponsibleUserId;
    const nextDeadlineError = !followUpDeadline;

    setFollowUpActivityTypeError(nextTypeError);
    setFollowUpActivityTitleError(nextTitleError);
    setFollowUpResponsibleUserError(nextResponsibleUserError);
    setFollowUpDeadlineError(nextDeadlineError);

    if (
      nextTypeError ||
      nextTitleError ||
      nextResponsibleUserError ||
      nextDeadlineError ||
      !projectDetails?.contactPerson
    ) {
      return;
    }

    const payload: CreateActivityDto = {
      contactPersonId: projectDetails.contactPerson.contactPersonId,
      responsibleUserId: Number(followUpResponsibleUserId),
      activityType: followUpActivityType,
      title: followUpActivityTitle.trim(),
      description: followUpActivityDescription.trim() || null,
      deadline: followUpDeadline.toDate(),
      isRecurring: false,
    };

    try {
      const response = await createFollowUpActivity(payload);
      await updateProjectClosureWaiver("", true, false, false);
      setFollowUpActivity(response.data);
      setWaiveFollowUpActivity(false);
      showSuccessMessage("Folgeaktivität erfolgreich erstellt!");
      setFollowUpDialogOpen(false);
    } catch {
      showErrorMessage("Folgeaktivität konnte nicht vollständig gespeichert werden!");
    }
  };

  const handleCompleteProject = async () => {
    setIsCompletingProject(true);
    try {
      await completeProject(
        projectCompletionNoteDraft.trim(),
        followUpActivity?.activityId ?? null,
        waiveFollowUpActivity ? projectClosureWaiverDraft.trim() : null
      );
    } finally {
      setIsCompletingProject(false);
    }
  };

  const effectiveWaiverReason = waiveFollowUpActivity ? projectClosureWaiverDraft : null;
  const hasValidWaiverReason = (effectiveWaiverReason?.trim().length ?? 0) >= PROJECT_CLOSURE_WAIVER_REASON_MIN_LENGTH;
  const isCompleteReady = canCompleteProject(
    projectBillingDetails,
    projectCompletionNoteDraft,
    hasValidWaiverReason ? effectiveWaiverReason : null,
    hasPlannedFollowUpActivity
  );
  const canTriggerComplete = projectBillingDetails?.status === "Abrechnung" && (isPL || hasPermissionProjectBilling);
  // The note only exists from billing onward and becomes read-only immediately after completion.
  const shouldShowProjectCompletionNote = projectStatus === "Abrechnung" || projectStatus === "Abgeschlossen";
  const canEditProjectCompletionNote =
    projectStatus === "Abrechnung" && (isPL || hasPermissionProjectBilling || hasProjectPermission);
  const isProjectCompletionNoteDirty = projectCompletionNoteDraft.trim() !== savedProjectCompletionNote.trim();
  const canEditClosureGuard =
    projectStatus === "Abrechnung" && (isPL || hasPermissionProjectBilling || hasProjectPermission);
  const effectiveSavedWaiverReason = savedProjectClosureWaiverReason.trim();
  const effectiveCurrentWaiverReason = waiveFollowUpActivity ? projectClosureWaiverDraft.trim() : "";
  const currentWaiverReasonLength = effectiveCurrentWaiverReason.length;
  const isProjectClosureWaiverDirty = effectiveCurrentWaiverReason !== effectiveSavedWaiverReason;
  const canSaveProjectClosureWaiver =
    canEditClosureGuard &&
    !hasPlannedFollowUpActivity &&
    isProjectClosureWaiverDirty &&
    (!waiveFollowUpActivity || currentWaiverReasonLength >= PROJECT_CLOSURE_WAIVER_REASON_MIN_LENGTH);
  const hasProjectContactPerson = Boolean(projectDetails?.contactPerson);
  const isProjectContactPersonBlockedForActivities =
    projectDetails?.contactPerson?.crmContactAllowed === false ||
    projectDetails?.contactPerson?.crmContactStatus === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED;
  const canAddFollowUpActivity =
    canEditClosureGuard &&
    hasProjectContactPerson &&
    !isProjectContactPersonBlockedForActivities &&
    !hasPlannedFollowUpActivity &&
    !waiveFollowUpActivity;
  const canSelectProjectClosureWaiver = canEditClosureGuard && !hasPlannedFollowUpActivity;
  const addFollowUpActivityTooltip = isProjectContactPersonBlockedForActivities
    ? "Für diese Ansprechperson ist kein weiterer Kontakt gewünscht."
    : hasPlannedFollowUpActivity
    ? "Es wurde bereits eine Folgeaktivität geplant."
    : waiveFollowUpActivity
    ? "Der Verzicht ist bereits ausgewählt."
    : "";

  return (
    <Container sx={{ marginBottom: 4 }}>
      {projectBillingDetails?.status === "Durchführung" && (isPL || hasPermissionProjectBilling) ? (
        <Button
          variant="outlined"
          color="success"
          sx={{ fontWeight: 600, mr: 2 }}
          onClick={handleOpenBillingDialog}
          startIcon={<Payments />}
          size="small"
        >
          Abrechnung
        </Button>
      ) : null}
      {canTriggerComplete ? (
        <Tooltip
          title={
            isCompleteReady
              ? ""
              : `Alle Punkte der Abrechnungs-Checkliste, Abschlussnotizen mit mindestens ${PROJECT_COMPLETION_NOTE_MIN_LENGTH} Zeichen und entweder eine Folgeaktivität oder ein begründeter Verzicht sind erforderlich.`
          }
        >
          <span>
            <Button
              variant="outlined"
              color="success"
              sx={{ fontWeight: 600, mr: 2 }}
              onClick={handleCompleteProject}
              startIcon={<CheckCircle />}
              size="small"
              disabled={!isCompleteReady || isCompletingProject}
            >
              Projekt abschließen
            </Button>
          </span>
        </Tooltip>
      ) : null}
      <BillingDialog
        billingDialogOpen={billingDialogOpen}
        handleCloseBillingDialog={handleCloseBillingDialog}
        handleStartBilling={handleStartBilling}
      />
      <BillingWarningDialog
        billingWarningDialogOpen={billingWarningDialogOpen}
        handleCloseBillingWarning={handleCloseBillingWarning}
      />
      <BTAllocationDialog
        btAllocationDialogOpen={btAllocationDialogOpen}
        handleCloseBTAllocationDialog={handleCloseBTAllocationDialog}
        saveBTAllocation={handleSaveBTAllocation}
        projectMembers={projectBillingDetails?.membersBilling || []}
        soldBT={projectBillingDetails?.soldBT || 0}
        soldExpenses={projectBillingDetails?.soldExpenses || 0}
      />
      <ActivityFormDialog
        open={followUpDialogOpen}
        title="Folgeaktivität hinzufügen"
        submitLabel="Folgeaktivität erstellen"
        onClose={() => setFollowUpDialogOpen(false)}
        onSubmit={handleCreateFollowUpActivity}
        isSubmitting={isCreatingFollowUpActivity}
        contactPersonName={projectDetails?.contactPerson?.name}
        companyName={projectDetails?.client?.name}
        activityType={followUpActivityType}
        onActivityTypeChange={setFollowUpActivityType}
        activityTypeError={followUpActivityTypeError}
        activityTitle={followUpActivityTitle}
        onActivityTitleChange={(value) => {
          setFollowUpActivityTitle(value);
          setFollowUpActivityTitleError(false);
        }}
        activityTitleError={followUpActivityTitleError}
        activityDescription={followUpActivityDescription}
        onActivityDescriptionChange={setFollowUpActivityDescription}
        responsibleUserId={followUpResponsibleUserId}
        onResponsibleUserChange={setFollowUpResponsibleUserId}
        responsibleUserError={followUpResponsibleUserError}
        responsibleUserLabel="Verantwortlich"
        members={closureActivityMembers}
        activityDeadline={followUpDeadline}
        onActivityDeadlineChange={setFollowUpDeadline}
        deadlineError={followUpDeadlineError}
        idPrefix="project-closure-follow-up"
      />
      <Stack>
        <Typography variant="body1" fontSize={18} fontWeight={"bold"} sx={{ mb: 2 }}>
          Bestätigung von Dokumenten
        </Typography>
        {projectBillingDetails ? (
          <ProjectDocumentDetails
            projectBillingDetailsDto={projectBillingDetails}
            hasPermissionDocumentFiling={hasPermissionDocumentFiling}
            hasPermissionContractFiling={hasPermissionContractFiling}
            hasPermissionProjectBilling={hasPermissionProjectBilling}
            changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
            changeProjectMemberBillingDetails={changeProjectMemberBillingDetails}
          />
        ) : null}
      </Stack>
      {shouldShowProjectCompletionNote ? (
        <ProjectCompletionNoteCard
          note={projectCompletionNoteDraft}
          onChange={setProjectCompletionNoteDraft}
          onSave={canEditProjectCompletionNote ? handleSaveProjectCompletionNote : undefined}
          editable={canEditProjectCompletionNote}
          saveDisabled={!isProjectCompletionNoteDirty || isCompletingProject}
          saveLoading={isSavingProjectCompletionNote}
          minLength={PROJECT_COMPLETION_NOTE_MIN_LENGTH}
        />
      ) : null}
      {projectStatus === "Abrechnung" || projectStatus === "Abgeschlossen" ? (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ marginBottom: 4, borderColor: "primary.main" }} />
          <Stack spacing={2}>
            <Typography variant="body1" fontSize={18} fontWeight={"bold"}>
              Folgeaktivität zum Projektabschluss
            </Typography>
            <Typography variant="body2">
              Beim Projektabschluss muss entweder eine Folgeaktivität für die Ansprechperson geplant werden oder ein
              begründeter Verzicht vorliegen.
            </Typography>
            {hasPlannedFollowUpActivity ? (
              <Box
                sx={{
                  alignItems: "center",
                  bgcolor: "success.light",
                  border: "1px solid",
                  borderColor: "success.main",
                  borderRadius: 1,
                  color: "success.contrastText",
                  display: "flex",
                  gap: 1,
                  px: 2,
                  py: 1,
                  width: "fit-content",
                }}
              >
                <CheckCircle fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  {followUpActivity
                    ? `Folgeaktivität geplant: ${followUpActivity.title || "Ohne Titel"} am ${new Date(
                        followUpActivity.deadline
                      ).toLocaleDateString("de-DE")}`
                    : "Folgeaktivität geplant"}
                </Typography>
              </Box>
            ) : null}
            {hasProjectContactPerson ? (
              <Tooltip title={addFollowUpActivityTooltip}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddTask />}
                    sx={{ alignSelf: "flex-start" }}
                    onClick={handleOpenFollowUpDialog}
                    disabled={!canAddFollowUpActivity}
                  >
                    Folgeaktivität hinzufügen
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Typography variant="body2" color="error">
                Für dieses Projekt ist keine Ansprechperson hinterlegt. Der Abschluss ist daher nur mit begründetem
                Verzicht möglich.
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={waiveFollowUpActivity}
                  onChange={(event) => setWaiveFollowUpActivity(event.target.checked)}
                  disabled={!canSelectProjectClosureWaiver}
                />
              }
              label="Ohne Folgeaktivität abschließen"
            />
            <TextField
              label="Begründung für den Verzicht"
              value={projectClosureWaiverDraft}
              onChange={(event) => setProjectClosureWaiverDraft(event.target.value)}
              disabled={!waiveFollowUpActivity || !canSelectProjectClosureWaiver}
              multiline
              minRows={3}
              fullWidth
              required={waiveFollowUpActivity}
              helperText={
                waiveFollowUpActivity
                  ? `${currentWaiverReasonLength}/${PROJECT_CLOSURE_WAIVER_REASON_MIN_LENGTH}`
                  : undefined
              }
            />
            {canEditClosureGuard ? (
              <Button
                variant="contained"
                size="small"
                sx={{ alignSelf: "flex-start" }}
                onClick={handleSaveProjectClosureWaiver}
                disabled={!canSaveProjectClosureWaiver || isCompletingProject}
              >
                {isSavingProjectClosureWaiver ? "Speichert..." : "Verzicht speichern"}
              </Button>
            ) : null}
          </Stack>
        </Box>
      ) : null}
      {(projectBillingDetails?.status === "Abrechnung" || projectBillingDetails?.status === "Abgeschlossen") &&
      projectBillingDetails?.soldBT != null &&
      projectBillingDetails?.euroPerBT != null &&
      projectBillingDetails?.soldExpenses != null ? (
        <Box>
          <Divider sx={{ marginTop: 4, marginBottom: 4, borderColor: "primary.main" }} />
          <Stack>
            <Typography variant="body1" fontSize={18} fontWeight={"bold"} sx={{ mb: 2 }}>
              Informationen zur Projektabrechnung {projectBillingDetails.projectName}
            </Typography>
            <ProjectBillingDetails
              soldBT={projectBillingDetails?.soldBT}
              euroPerBT={projectBillingDetails?.euroPerBT}
              soldExpenses={projectBillingDetails?.soldExpenses}
            />
          </Stack>
          <Divider sx={{ marginTop: 4, marginBottom: 4, borderColor: "primary.main" }} />
          <Stack direction={"column"}>
            <Stack direction={"row"} spacing={10} alignItems={"center"} sx={{ mb: 2 }}>
              <Typography variant="body1" fontSize={18} fontWeight={"bold"}>
                Aufteilung innerhalb des Teams
              </Typography>
              {projectBillingDetails?.status === "Abrechnung" && (isPL || hasPermissionProjectBilling) ? (
                <Button
                  variant="contained"
                  sx={{ maxWidth: 250 }}
                  size="small"
                  color="secondary"
                  onClick={handleOpenBTAllocationDialog}
                >
                  <Edit fontSize="small" sx={{ marginRight: 2 }} /> BT-Aufteilung eingeben
                </Button>
              ) : null}
            </Stack>
            <BTAllocation
              soldBT={projectBillingDetails?.soldBT}
              euroPerBT={projectBillingDetails?.euroPerBT}
              soldExpenses={projectBillingDetails?.soldExpenses}
              projectMembers={projectBillingDetails?.membersBilling}
            />
          </Stack>
        </Box>
      ) : null}
    </Container>
  );
};

export default ProjectBilling;
